/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, isEqual } from 'lodash-es';

import { injectScript } from 'client/utils/inject-script';
import { InventoryEntities } from 'client/data/models/inventory';
import { getCreativeId } from 'client/engagement-handlers/helper';
import { isAutonation } from 'site-modules/shared/utils/dealer-details-utils';
import { DealerInfoModel, DealerInfoEntities, getDealerInfoPath } from 'client/data/models/dealer-info';
import {
  CARCODE_CONFIG,
  CARCODE_CONFIG_ENTITY,
  CARCODE_SDK_INITIALIZED_MESSAGE,
} from 'site-modules/shared/constants/carcode';
import { TrackingConstant } from 'client/tracking/constant';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { ActivEngageChat } from 'site-modules/shared/components/activ-engage-chat/activ-engage-chat';
import { PageModel } from 'client/data/models/page';
import { logger } from 'client/utils/logger/logger';
import { withCarcodeConfigCheck } from 'site-modules/shared/components/inventory/with-carcode-config-check/with-carcode-config-check';

/**
 * Get carcode tracking data
 * @param {string} franchiseId
 * @param {string} pageName
 * @param {string} pageCategory
 * @param {Node} wrapper
 * @return {Object}
 */
export function getCarCodeTrackingParams(franchiseId, pageName, pageCategory, wrapper) {
  return {
    gtm: {
      synpartner: TrackingConstant.SYNPARTNER,
      page_name: pageName,
      page_category: pageCategory,
      creative_id: getCreativeId(wrapper),
      event_data: {
        partner_id: franchiseId,
      },
    },
  };
}

export const getActivEngageChatConfig = (dealershipInfo, launchSource) => {
  const dealerWebSite = get(dealershipInfo, 'dealerWebSites[0]', '');
  const parentDealershipName = get(dealershipInfo, 'parentDealershipName', '');
  return isAutonation(parentDealershipName)
    ? {
        launchSource,
        dealerWebSite,
      }
    : null;
};

export class CarcodeButtonUI extends Component {
  static propTypes = {
    customConfig: CARCODE_CONFIG_ENTITY,
    dealerInfo: InventoryEntities.DealerInfo,
    dealershipInfo: DealerInfoEntities.DealerInfo,
    carCodeWidgetsUrl: PropTypes.string.isRequired,
    page: PropTypes.shape({
      name: PropTypes.string,
      category: PropTypes.string,
    }),
    legacy: PropTypes.shape({
      pageName: PropTypes.string,
      categoryName: PropTypes.string,
    }),
    toggleBtnText: PropTypes.string,
    activEngageConfigLaunchSource: PropTypes.string,
  };

  static defaultProps = {
    customConfig: {},
    dealerInfo: null,
    dealershipInfo: null,
    page: null,
    legacy: null,
    toggleBtnText: null,
    activEngageConfigLaunchSource: '',
  };

  componentDidMount() {
    const {
      dealerInfo,
      dealershipInfo,
      carCodeWidgetsUrl,
      page,
      legacy,
      toggleBtnText,
      customConfig,
      activEngageConfigLaunchSource,
    } = this.props;
    const rooftopId = get(dealerInfo, 'rooftopId', '');
    const franchiseId = get(dealerInfo, 'franchiseId', '');
    const src = `${carCodeWidgetsUrl}/edmunds/${rooftopId}.js`;
    const legacyPageName = get(legacy, 'pageName', '');
    const legacyCategoryName = get(legacy, 'categoryName', '');
    const pageName = get(page, 'name', '');
    const pageCategory = get(page, 'category', '');

    window.__carcode = {
      tracking: getCarCodeTrackingParams(
        franchiseId,
        legacyPageName || pageName,
        legacyCategoryName || pageCategory,
        this.wrapper
      ),
      ...CARCODE_CONFIG,
      ...customConfig,
    };

    if (toggleBtnText) {
      window.__carcode.buttonText = toggleBtnText;
    }

    this.initActivEngageChat(getActivEngageChatConfig(dealershipInfo, activEngageConfigLaunchSource));

    window.addEventListener('message', this.carcodeMessagesListener);

    injectScript(src).catch(() => {
      logger.warn('Inject CarCode script error');
    });
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.dealershipInfo, this.props.dealershipInfo)) {
      this.destroyActivEngageChat();

      const { dealershipInfo, activEngageConfigLaunchSource } = this.props;

      this.initActivEngageChat(getActivEngageChatConfig(dealershipInfo, activEngageConfigLaunchSource));
    }
  }

  componentWillUnmount() {
    const event = new CustomEvent('carcode', { detail: 'destroy' });
    document.dispatchEvent(event);
    window.removeEventListener('message', this.carcodeMessagesListener);
  }

  setWrapper = ref => {
    this.wrapper = ref;
  };

  widget = null;

  initActivEngageChat = config => {
    if (config) {
      this.activeEngageChatController = new ActivEngageChat({ ...config });
      this.activeEngageChatController.init();
      window.__carcode.chatClickHandler = () => this.activeEngageChatController.startChat();
    }
  };

  destroyActivEngageChat = () => {
    this.activeEngageChatController = null;
    window.__carcode.chatClickHandler = null;
  };

  carcodeMessagesListener = event => {
    if (event.data?.type === CARCODE_SDK_INITIALIZED_MESSAGE) {
      this.widget = new window.CarcodeWidget();
      window.removeEventListener('message', this.carcodeMessagesListener);
    }
  };

  render() {
    const { dealerInfo } = this.props;
    const franchiseId = get(dealerInfo, 'franchiseId', '');
    const rooftopId = get(dealerInfo, 'rooftopId', '');

    return <span ref={this.setWrapper} data-dealer-franchise-id={franchiseId} data-dealer-rooftop-id={rooftopId} />;
  }
}

/**
 * Taking from redux state directly
 * @param state
 */
export const mapStateToProps = state => ({
  carCodeWidgetsUrl: state.config.carCodeWidgetsUrl,
});

export const stateToPropsConfig = {
  page: bindToPath('page', PageModel),
  legacy: bindToPath('legacy', PageModel),
  dealershipInfo: bindToPath(({ dealerInfo }) => getDealerInfoPath({ dealerInfo }), DealerInfoModel),
};

export const CarcodeButton = connect(mapStateToProps)(
  connectToModel(withCarcodeConfigCheck(CarcodeButtonUI), stateToPropsConfig)
);
